import { Typography } from "@mui/material";
import { CoreRadioCardIcon, CoreRadioCardIconProps } from "../card/RadioCard";
import styles from "./FileCard.module.css";
import FileUploadIcon from "./images/file-attachment.svg?react";
import { CoreButton, CoreIconButton } from "../button/Button";
import i18nPackage from "../../i18n/i18n";
import CoreDeleteIcon from "../icon/DeleteIcon";
import CoreNewTabIcon from "../icon/NewTabIcon";
import ErrorIcon from "./images/error-filled.svg?react";
import CheckmarkIcon from "../images/checkmark.svg?react";
import React, { useEffect, useState } from "react";
import useOpenInNewTab from "./useOpenInNewTab";
import { triggerFileDownload } from "./fileHelper";
import { CoreDownloadIcon, CoreRetryIcon } from "../icon";

export type FileRadioIconProps = Omit<CoreRadioCardIconProps, "icon" | "enabled">;

export function FileRadioIcon(props?: FileRadioIconProps) {
	return <CoreRadioCardIcon {...props} icon={<FileUploadIcon aria-hidden="true" />} enabled={true} />;
}

export function FileCardError({
	message,
	className,
	labelClassName,
}: {
	message: string;
	className?: string;
	labelClassName?: string;
}) {
	return (
		<div className={`${styles.statusContainer} ${className || ""}`}>
			<ErrorIcon />
			<Typography className={labelClassName}>{message}</Typography>
		</div>
	);
}

function extractFileType(fileName?: string): string | undefined {
	if (!fileName) return undefined;
	const parts = fileName.split(".");
	if (parts.length === 1) return undefined;
	return parts[parts.length - 1].toUpperCase();
}

function formatFileSize(bytes: number): string {
	if (bytes < 1000000) return `${Math.round(bytes / 1000)}KB`;
	return `${Math.round(bytes / 1000000)}MB`;
}

function RetryIconWrapper({ shouldShowRetry, children }: { shouldShowRetry: boolean; children: React.ReactNode }) {
	if (shouldShowRetry) {
		return <CoreRetryIcon />;
	}
	return children;
}

export interface CoreFileItem {
	name: string;
	size: number;
	type?: string;
	url?: string;
	expiresAt?: number;
	nativeFile?: File;
}

type CoreFileCardProps = {
	file: CoreFileItem;
	showDownload?: boolean;
	showOpenInNewTab?: boolean;
	onRemove?: () => void;
	onRefetch?: () => Promise<CoreFileItem | undefined>;
	errorMessage?: string;
	showCompleteMessage?: boolean;
};

export default function CoreFileCard({
	file,
	showDownload,
	showOpenInNewTab,
	onRemove,
	errorMessage,
	showCompleteMessage,
	onRefetch,
}: CoreFileCardProps) {
	const { name, size, expiresAt } = file;
	const [now, setNow] = useState(Date.now());
	const [isRefetching, setIsRefetching] = useState<boolean>(false);

	const fileType = extractFileType(name);

	const { canOpenInTab, handleOpenInNewTab: openInNewTab } = useOpenInNewTab(file);

	useEffect(() => {
		if (!expiresAt) return;

		const interval = setInterval(() => {
			setNow(Date.now());
		}, 10000); // Update every 10s

		return () => clearInterval(interval);
	}, [expiresAt]);

	const isExpired = expiresAt ? now > expiresAt : false;
	const shouldShowRetryIcon = Boolean(isExpired && onRefetch);

	const handleFileDownload = async () => {
		try {
			let fileToDownload = file;
			if (shouldShowRetryIcon) {
				setIsRefetching(true);
				const newFile = await onRefetch?.();
				if (newFile) {
					fileToDownload = newFile;
				}
			}

			await triggerFileDownload(fileToDownload);
		} catch (e) {
			console.error("Error downloading file:", e);
		} finally {
			setIsRefetching(false);
		}
	};

	const handleOpenInNewTab = async () => {
		try {
			let fileToOpen = file;
			if (shouldShowRetryIcon) {
				setIsRefetching(true);
				const newFile = await onRefetch?.();
				if (newFile) {
					fileToOpen = newFile;
				}
			}

			openInNewTab(fileToOpen);
		} catch (e) {
			console.error("Error opening file in new tab:", e);
		} finally {
			setIsRefetching(false);
		}
	};

	return (
		<div className={styles.fileCard}>
			<div className={styles.fileInfoSection}>
				<FileRadioIcon />
				<div className={styles.fileDetailsCol}>
					<Typography className={styles.fileName}>{name}</Typography>
					<div className={styles.fileInfoRow}>
						{fileType && (
							<>
								<Typography>{fileType}</Typography>
								<Typography>|</Typography>
							</>
						)}
						<Typography>{formatFileSize(size)}</Typography>
						{errorMessage && (
							<>
								<Typography>|</Typography>
								<FileCardError message={errorMessage} />
							</>
						)}
						{showCompleteMessage && (
							<>
								<Typography>|</Typography>
								<div className={styles.statusContainer}>
									<div className={styles.checkMarkWrapper}>
										<CheckmarkIcon />
									</div>
									<Typography>{i18nPackage.t("coreComponents.fileCard.completed")}</Typography>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
			<div className={styles.fileActionSection}>
				{showDownload && (
					<CoreButton
						onClick={handleFileDownload}
						className={styles.downloadButton}
						startIcon={
							<RetryIconWrapper shouldShowRetry={shouldShowRetryIcon}>
								<span aria-hidden="true">
									<CoreDownloadIcon />
								</span>
							</RetryIconWrapper>
						}
						loading={isRefetching}
						disabled={isRefetching}
					>
						{i18nPackage.t("coreComponents.fileCard.download")}
					</CoreButton>
				)}
				{showOpenInNewTab && (
					<CoreIconButton
						onClick={handleOpenInNewTab}
						color="primary"
						tooltip={
							canOpenInTab
								? i18nPackage.t("coreComponents.fileCard.openInNewTab")
								: i18nPackage.t("coreComponents.fileCard.download")
						}
						className={styles.fileActionButton}
						variant="plain"
						disabled={isRefetching}
					>
						<RetryIconWrapper shouldShowRetry={shouldShowRetryIcon}>
							{canOpenInTab ? <CoreNewTabIcon /> : <CoreDownloadIcon />}
						</RetryIconWrapper>
					</CoreIconButton>
				)}
				{onRemove && (
					<CoreIconButton
						variant="plain"
						onClick={onRemove}
						color="cancel"
						tooltip={i18nPackage.t("coreComponents.fileCard.remove")}
						className={styles.fileActionButton}
					>
						<CoreDeleteIcon />
					</CoreIconButton>
				)}
			</div>
		</div>
	);
}
